// src/seo/pageSeo.js
// 各页面类型的 TDK 与 JSON-LD 构建器。
// 字段提取做了「多候选名 + 兜底」处理，能适配接口字段的细微差异；
// 接入真实接口后，如某字段名不同，集中在此处微调即可。
import { setMeta, setSchema } from '@/utils/seo'
import { SITE, siteUrl, toAbsoluteUrl, toIso } from '@/seo/site'

// ---- 通用取值助手 ----
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k]
  }
  return ''
}
const firstImg = (v) => (Array.isArray(v) ? v[0] || '' : v || '')
const truncate = (s, n = 120) => {
  s = String(s || '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return s.length > n ? s.slice(0, n) + '…' : s
}

const publisher = () => ({
  '@type': 'Organization',
  name: SITE.name,
  logo: { '@type': 'ImageObject', url: toAbsoluteUrl(SITE.organization.logo) },
})

const brand = () => ({ '@type': 'Brand', name: SITE.name })

// 面包屑（BreadcrumbList）：items 为当前页之前的层级，[{name, url}]
function setBreadcrumb(items) {
  const list = [
    { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl('/home') },
    ...items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 2,
      name: it.name,
      item: siteUrl(it.url),
    })),
  ]
  setSchema('breadcrumb', { '@type': 'BreadcrumbList', itemListElement: list })
}

// ============ 首页 ============
export function applyHomeSeo() {
  const title = '衣创社区'
  const description =
    '衣创社区是面向服装设计师的一站式创作与交易平台，提供面辅料馆、设计师商城、设计赛事与行业资讯，连接设计师、面辅料商与品牌。'
  const keywords = '衣创社区,服装设计,面辅料,设计师商城,服装赛事,服装资讯,原创设计'
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl('/home'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: `${SITE.name} - 设计师面料商城与服装创作社区`,
    description,
    url: siteUrl('/home'),
  })
}

// ============ 面辅料馆：列表 / 搜索 ============
export function applyFabricListSeo(opts = {}) {
  const kw = opts.keyword || opts.category || opts.material || ''
  const title = kw ? `${kw}面辅料采购_衣创社区面辅料馆` : '面辅料馆_衣创社区'
  const description = kw
    ? `衣创社区面辅料馆提供${kw}等各类服装面辅料在线选购、参数查询与样品申请，支持按品类、材质、工艺筛选。`
    : '衣创社区面辅料馆汇集海量服装面辅料，支持在线选购、参数查询与样品申请，是设计师找面料的首选平台。'
  const keywords = ['面辅料', kw, '服装面料', '辅料采购', '面料商城'].filter(Boolean).join(',')
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(opts.path || '/fabricHall'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: title,
    description,
    url: siteUrl(opts.path || '/fabricHall'),
  })
}

// ============ 面辅料：详情 ============
export function applyFabricSeo(d) {
  if (!d) return
  const name = pick(d, ['fabric_name_cn', 'goods_name', 'accessory_name', 'name', 'title'])
  const image = toAbsoluteUrl(firstImg(d.image || d.cover || d.images || d.main_image || d.cover_image))
  const desc = truncate(pick(d, ['description', 'desc', 'summary', 'remark', 'content']))
  const price = pick(d, ['price', 'sale_price', 'min_price', 'price_range'])
  const title = name || '面辅料详情'
  const description = desc || `查看${name}的详细参数、规格、价格与样品申请信息。`
  const keywords = [name, '面辅料', '面料详情', '服装面料'].filter(Boolean).join(',')
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(d.path || '/fabricHallDetail'),
    image,
    type: 'product',
  })
  const offers = price !== '' ? {
    '@type': 'Offer',
    priceCurrency: 'CNY',
    price: String(price),
    availability: 'https://schema.org/InStock',
    url: siteUrl(d.path || '/fabricHallDetail'),
  } : undefined
  setBreadcrumb([{ name: '面辅料馆', url: '/fabricHall' }])
  setSchema('page', {
    '@type': 'Product',
    name,
    image: image ? [image] : undefined,
    description: desc || undefined,
    brand: brand(),
    offers,
  })
}

// ============ 商城：列表 ============
export function applyMallListSeo(opts = {}) {
  const kw = opts.keyword || opts.category || ''
  const title = kw ? `${kw}_衣创社区设计师商城` : '设计师商城_衣创社区'
  const description = kw
    ? `衣创社区设计师商城提供${kw}等原创设计商品，汇聚独立设计师原创作品，支持在线购买。`
    : '衣创社区设计师商城汇聚独立设计师原创作品，服装、配饰、面料一站式购买。'
  const keywords = ['设计师商城', kw, '原创设计', '服装购买'].filter(Boolean).join(',')
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(opts.path || '/Mall'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: title,
    description,
    url: siteUrl(opts.path || '/Mall'),
  })
}

// ============ 商品：详情 ============
export function applyGoodsSeo(d) {
  if (!d) return
  const name = pick(d, ['goods_name', 'product_name', 'name', 'title'])
  const image = toAbsoluteUrl(firstImg(d.image || d.cover || d.images || d.main_image || d.goods_image))
  const desc = truncate(pick(d, ['description', 'desc', 'summary', 'remark', 'content', 'sub_title']))
  const price = pick(d, ['price', 'sale_price', 'min_price', 'price_range', 'current_price'])
  const title = name || '商品详情'
  const description = desc || `查看${name}的详情、规格、价格与购买信息。`
  const keywords = [name, '设计师商品', '原创设计', '服装购买'].filter(Boolean).join(',')
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(d.path || '/Mall/GoodsDetail'),
    image,
    type: 'product',
  })
  const offers = price !== '' ? {
    '@type': 'Offer',
    priceCurrency: 'CNY',
    price: String(price),
    availability: 'https://schema.org/InStock',
    url: siteUrl(d.path || '/Mall/GoodsDetail'),
  } : undefined
  setBreadcrumb([{ name: '设计师商城', url: '/Mall' }])
  setSchema('page', {
    '@type': 'Product',
    name,
    image: image ? [image] : undefined,
    description: desc || undefined,
    brand: brand(),
    offers,
  })
}

// ============ 设计师列表 ============
export function applyDesignerListSeo(opts = {}) {
  const title = '设计师大全_衣创社区'
  const description = '衣创社区设计师大全，汇集原创服装设计师与工作室，发现并关注你喜欢的独立设计师。'
  const keywords = '设计师,服装设计师,独立设计师,原创设计,衣创社区'
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(opts.path || '/Mall/DesignerList'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: title,
    description,
    url: siteUrl(opts.path || '/Mall/DesignerList'),
  })
}

// ============ 资讯：列表 ============
export function applyInfoListSeo(opts = {}) {
  const kw = opts.category || opts.keyword || ''
  const title = kw ? `${kw}资讯_衣创社区` : '行业资讯_衣创社区'
  const description = kw
    ? `衣创社区${kw}资讯，提供最新的服装行业动态、趋势与设计干货。`
    : '衣创社区行业资讯，提供服装行业动态、趋势解读、设计干货与活动报道。'
  const keywords = ['服装资讯', kw, '服装行业', '设计趋势'].filter(Boolean).join(',')
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(opts.path || '/info'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: title,
    description,
    url: siteUrl(opts.path || '/info'),
  })
}

// ============ 文章 / 资讯：详情 ============
export function applyArticleSeo(d) {
  if (!d) return
  const headline = pick(d, ['title', 'article_title', 'name', 'subject'])
  const image = toAbsoluteUrl(firstImg(d.cover || d.image || d.images || d.thumbnail))
  const desc = truncate(pick(d, ['description', 'summary', 'intro', 'content', 'remark']))
  const body = truncate(pick(d, ['content', 'body']), 200)
  const author = pick(d, ['author', 'author_name', 'writer', 'source'])
  const published = toIso(pick(d, ['publish_time', 'created_at', 'create_time', 'add_time', 'time', 'publishTime']))
  const modified = toIso(pick(d, ['update_time', 'updated_at', 'edit_time']))
  const title = headline || '资讯详情'
  const description = desc || truncate(body) || `${title} - 衣创社区资讯。`
  setMeta({
    title,
    description,
    keywords: [headline, '服装资讯', '行业资讯'].filter(Boolean).join(','),
    canonical: siteUrl(d.path || '/info/detail'),
    image,
    type: 'article',
  })
  setSchema('page', {
    '@type': 'NewsArticle',
    headline,
    image: image ? [image] : undefined,
    datePublished: published || undefined,
    dateModified: modified || published || undefined,
    author: author ? { '@type': 'Person', name: author } : undefined,
    publisher: publisher(),
    description: desc || undefined,
    articleBody: body || undefined,
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl(d.path || '/info/detail') },
  })
  setBreadcrumb([{ name: '行业资讯', url: '/info' }])
}

// ============ 赛事：列表 ============
export function applyContestListSeo(opts = {}) {
  const title = '设计赛事_衣创社区'
  const description = '衣创社区设计赛事中心，发布服装设计大赛、征稿活动与作品评选，助力设计师展示才华、赢取奖金与资源。'
  const keywords = '服装设计大赛,设计赛事,征稿,服装比赛,衣创社区'
  setMeta({
    title,
    description,
    keywords,
    canonical: siteUrl(opts.path || '/contest'),
    type: 'website',
  })
  setSchema('page', {
    '@type': 'CollectionPage',
    name: title,
    description,
    url: siteUrl(opts.path || '/contest'),
  })
}

// ============ 赛事：详情（Event） ============
export function applyContestSeo(d) {
  if (!d) return
  const name = pick(d, ['contest_name', 'match_name', 'name', 'title', 'subject'])
  const image = toAbsoluteUrl(firstImg(d.cover || d.image || d.images || d.poster))
  const desc = truncate(pick(d, ['description', 'desc', 'summary', 'intro', 'content', 'rule']))
  const start = toIso(pick(d, ['start_time', 'begin_time', 'sign_up_start', 'startTime']))
  const end = toIso(pick(d, ['end_time', 'end_time', 'sign_up_end', 'endTime']))
  const title = name || '赛事详情'
  const description = desc || `查看${name}的赛程、奖项、报名方式与参赛规则。`
  setMeta({
    title,
    description,
    keywords: [name, '服装设计大赛', '设计赛事', '征稿'].filter(Boolean).join(','),
    canonical: siteUrl(d.path || '/contest/detail'),
    image,
    type: 'website',
  })
  setBreadcrumb([{ name: '设计赛事', url: '/contest' }])
  setSchema('page', {
    '@type': 'Event',
    name,
    startDate: start || undefined,
    endDate: end || undefined,
    eventStatus: start ? 'https://schema.org/EventScheduled' : undefined,
    description: desc || undefined,
    image: image ? [image] : undefined,
    organizer: { '@type': 'Organization', name: SITE.name },
    location: { '@type': 'Place', name: pick(d, ['city', 'address', 'location']) || '线上/全国' },
  })
}

// ============ 帖子 / 社区：详情 ============
export function applyPostSeo(d) {
  if (!d) return
  const headline = pick(d, ['title', 'subject', 'name', 'content_title'])
  const image = toAbsoluteUrl(firstImg(d.cover || d.image || d.images || d.thumbnail))
  const desc = truncate(pick(d, ['description', 'summary', 'content']))
  const author = pick(d, ['user_name', 'author', 'nickname', 'author_name'])
  const published = toIso(pick(d, ['create_time', 'created_at', 'publish_time', 'time']))
  const title = headline || '社区帖子'
  const description = desc || `${title} - 衣创社区设计师讨论区。`
  setMeta({
    title,
    description,
    keywords: [headline, '服装社区', '设计师交流'].filter(Boolean).join(','),
    canonical: siteUrl(d.path || '/'),
    image,
    type: 'article',
  })
  setBreadcrumb([{ name: '社区', url: '/' }])
  setSchema('page', {
    '@type': 'DiscussionForumPosting',
    headline,
    image: image ? [image] : undefined,
    datePublished: published || undefined,
    author: author ? { '@type': 'Person', name: author } : undefined,
    interactionStatistic: pick(d, ['view_count', 'views'])
      ? {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/ViewAction',
          userInteractionCount: String(pick(d, ['view_count', 'views'])),
        }
      : undefined,
  })
}

// ============ 设计师 / 用户：主页（Person） ============
export function applyUserSeo(d) {
  if (!d) return
  const name = pick(d, ['nickname', 'user_name', 'name', 'real_name', 'designer_name'])
  const image = toAbsoluteUrl(firstImg(d.avatar || d.image || d.head_img || d.logo))
  const desc = truncate(pick(d, ['intro', 'description', 'signature', 'remark', 'bio']))
  const title = name ? `${name}的主页_衣创社区` : '设计师主页'
  const description = desc || `查看${name || '设计师'}在衣创社区的主页，了解其原创作品、动态与联系方式。`
  setMeta({
    title,
    description,
    keywords: [name, '服装设计师', '独立设计师'].filter(Boolean).join(','),
    canonical: siteUrl(d.path || ''),
    image,
    type: 'profile',
  })
  setBreadcrumb([{ name: '设计师', url: '/Mall/DesignerList' }])
  setSchema('page', {
    '@type': 'Person',
    name,
    image: image ? [image] : undefined,
    description: desc || undefined,
    url: siteUrl(d.path || ''),
    jobTitle: pick(d, ['job_title', 'title', 'role']) || '服装设计师',
  })
}

// ============ 案例：详情 ============
export function applyCaseSeo(d) {
  if (!d) return
  const name = pick(d, ['title', 'case_name', 'name', 'subject'])
  const image = toAbsoluteUrl(firstImg(d.cover || d.image || d.images))
  const desc = truncate(pick(d, ['description', 'summary', 'content']))
  const title = name || '合作案例'
  setMeta({
    title,
    description: desc || `查看${name}合作案例详情。`,
    keywords: [name, '服装合作案例', '设计案例'].filter(Boolean).join(','),
    canonical: siteUrl(d.path || '/CooperationCases'),
    image,
    type: 'article',
  })
  setBreadcrumb([{ name: '合作案例', url: '/CooperationCases' }])
  setSchema('page', {
    '@type': 'CreativeWork',
    name,
    image: image ? [image] : undefined,
    description: desc || undefined,
    creator: { '@type': 'Organization', name: SITE.name },
  })
}
